/**
 * QR Type Detector
 * Automatically detects whether a scanned QR code is:
 * - Employee time tracking QR (from phone app)
 * - Loyalty pass QR (customer wallet)
 * - Coupon QR
 */

export type QRType = 'employee' | 'loyalty' | 'coupon' | 'unknown';

export interface QRDetectionResult {
  type: QRType;
  data: string;
  extractedToken?: string;
  extractedPassId?: string;
}

/**
 * Detects the type of QR code based on its content
 */
export function detectQRType(qrData: string): QRDetectionResult {
  if (!qrData || typeof qrData !== 'string') {
    return { type: 'unknown', data: qrData || '' };
  }

  const trimmedData = qrData.trim();

  // Employee QR format: contains /scan-arrival?token=<uuid>
  // Example: https://example.com/scan-arrival?token=550e8400-e29b-41d4-a716-446655440000
  if (trimmedData.includes('/scan-arrival?token=')) {
    const tokenMatch = trimmedData.match(/token=([a-f0-9-]{36})/i);
    return {
      type: 'employee',
      data: trimmedData,
      extractedToken: tokenMatch?.[1],
    };
  }

  // Coupon QR format: coupon_<offer_id>_<customer_identifier>
  // Example: coupon_550e8400-e29b-41d4-a716-446655440003_customer_data
  if (trimmedData.startsWith('coupon_')) {
    return {
      type: 'coupon',
      data: trimmedData,
      extractedPassId: trimmedData,
    };
  }

  // UUID format (Google Wallet pass ID)
  // Example: 550e8400-e29b-41d4-a716-446655440000
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidRegex.test(trimmedData)) {
    return {
      type: 'loyalty',
      data: trimmedData,
      extractedPassId: trimmedData,
    };
  }

  // Apple Wallet serial number (alphanumeric, typically shorter)
  // These are usually alphanumeric strings that don't match UUID format
  // We assume non-UUID strings that look like pass identifiers are Apple passes
  const alphanumericRegex = /^[a-zA-Z0-9_-]{8,64}$/;
  if (alphanumericRegex.test(trimmedData)) {
    return {
      type: 'loyalty',
      data: trimmedData,
      extractedPassId: trimmedData,
    };
  }

  return { type: 'unknown', data: trimmedData };
}

/**
 * Extract the token from an employee QR URL
 */
export function extractEmployeeToken(qrData: string): string | null {
  const match = qrData.match(/token=([a-f0-9-]{36})/i);
  return match?.[1] || null;
}

/**
 * Check if a string is a valid UUID
 */
export function isValidUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}
